import React, { useState } from "react";
import Style from "./Settings.module.css";
import Button from "../../components/controls/button/Button";
import Input from "../../components/controls/input/customInput/CustomInput";
import Logo from "../../assets/hanshi/full-lg.svg";
import DropDown from "../../components/controls/dropdown/DropdownFluent";
import UploadImage from "./UploadLogo";
import {
  countries,
  days,
  dateFormats,
  timeFormats,
  decimalSeperator,
  groupSeperator,
} from "../../helpers/utils";
import { useEffect } from "react";
import CustomSelect from "../../components/controls/select/CustomSelect";
import CurrencyListing from "currency-list";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import { Switch } from "@fluentui/react-components";

const Settings = (props) => {
  const {
    settings,
    updateLogo,
    updateAccountSettings,
    logoLoading,
    getSettingsRequest,
  } = props;
  const dispatch = useDispatch();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currencyList, setCurrencyList] = useState([]);
  const permissions = localStorage.getItem("permissions");
  const [activeTab, setActiveTab] = useState("general");

  //Data states
  const [companyName, setCompanyName] = useState(settings.companyName);
  const [country, setCountry] = useState(settings.country);
  const [address, setAddress] = useState(settings.address);
  const [dateFormat, setDateformat] = useState(settings.regionalSettings?.dateFormat);
  const [timeFormat, setTimeformat] = useState(settings.regionalSettings?.timeFormat);
  const [firstDayWeek, setFirstDay] = useState(settings.regionalSettings?.firstDayWeek);
  const [group, setGroupSeperator] = useState(settings.regionalSettings?.groupSeparator);
  const [decimal, setDecimalSeperator] = useState(settings.regionalSettings?.decimalSeparator);
  const [currency, setCurrency] = useState(settings.regionalSettings?.currency);
  const [isMfaRequired, setIsMfaRequired] = useState(settings.settings?.isMfaRequired);
  const [isFirstLoginResetPassword, setIsFirstLoginResetPassword] = useState(settings.settings?.isFirstLoginResetPassword);

  const handleSave = () => {
    const region = {
      dateFormat: dateFormat,
      timeFormat: timeFormat,
      firstDayWeek: firstDayWeek,
      decimalSeparator: decimal,
      groupSeparator: group,
    };
    if (currency) {
      if (typeof currency === "string") {
        region.currency = currency;
      } else {
        region.currency = currency.value;
      }
    }
    const payload = {
      companyName: companyName,
      address: address,
      country: country,
      themeColors: {
        primary: "",
        secondary: "",
      },
      regionalSettings: { ...region },
      settings: {
        isMfaRequired: isMfaRequired,
        isFirstLoginResetPassword: isFirstLoginResetPassword,
      },
    };
    if (region.groupSeparator !== region.decimalSeparator) {
      updateAccountSettings(payload);
    } else {
      return dispatch(
        generalActions.showToast({
          title: "Decimal Seperator and group seperator can not be same.",
          type: "error",
        })
      );
    }
  };

  const isAnyInputSet = () => {
    return (
      companyName !== "" ||
      country !== "" ||
      address !== "" ||
      dateFormat !== "" ||
      timeFormat !== "" ||
      firstDayWeek !== "" ||
      group !== "" ||
      decimal !== "" ||
      currency !== ""
    );
  };

  useEffect(() => {
    !logoLoading && setShowUploadModal(false);
  }, [logoLoading]);

  useEffect(() => {
    const currencies = CurrencyListing.getAll("en_US");
    const currencyArray = Object.values(currencies);
    const transformedArray = currencyArray.map((obj) => ({
      label: `${obj.code} ${obj.symbol_native}`,
      value: `${obj.code} ${obj.symbol_native}`,
    }));
    setCurrencyList(transformedArray);
  }, []);

  const getDefaultValue = () => {
    if (typeof currency === "string") {
      return { label: currency, value: currency };
    } else {
      if (currency === null) {
        return { label: "USD $", value: "USD $" };
      }
      return { label: currency?.label, value: currency?.value };
    }
  };

  useEffect(() => {
    getSettingsRequest();
  }, []);

  return (
    <>
      <div className={Style.mainContainer}>
        <div className={`${Style.mainWrapper}`}>
          <h3 className="mt-4 mb-4">Account Settings</h3>
          <div className={Style.profileContainer}>
            {/* Tabs Navigation */}
            <div className={Style.tabsContainer}>
              <button
                className={`${Style.tabButton} ${activeTab === "general" ? Style.activeTab : ""}`}
                onClick={() => setActiveTab("general")}
              >
                General Settings
              </button>
              <button
                className={`${Style.tabButton} ${activeTab === "formats" ? Style.activeTab : ""}`}
                onClick={() => setActiveTab("formats")}
              >
                Date & Symbol Formats
              </button>
              <button
                className={`${Style.tabButton} ${activeTab === "security" ? Style.activeTab : ""}`}
                onClick={() => setActiveTab("security")}
              >
                Security Settings
              </button>
            </div>

            {/* Tab Content */}
            <div className={Style.tabContent}>
              {/* General Settings Tab */}
              {activeTab === "general" && (
                <div className={Style.tabPanel}>
                  <div className={Style.userInfoWrapper}>
                    <div className={Style.settinglogo}>
                      <img
                        src={settings.logoUrl !== null ? settings.logoUrl : Logo}
                        alt="Campany Logo"
                      />
                    </div>
                    <p
                      className={`${Style.userProfile} text-xsm-400`}
                      onClick={() => setShowUploadModal(true)}
                    >
                      Upload a new picture
                    </p>
                  </div>
                  <div className="mt-4">
                    <div className="d-flex align-items-end w-100 mb-3">
                      <Input
                        inputwd="w-50 me-3"
                        icon={false}
                        placeholder="Company name"
                        value={companyName}
                        type="text"
                        handleChange={setCompanyName}
                        label="Company name"
                      />
                      <CustomSelect
                        label="Country"
                        SelectText="Country"
                        selectWd="w-50"
                        options={countries.map((c) => ({ label: c.name, value: c.value }))}
                        searchText={()=>{}}
                        defaultVal={{ label: country || "Canada", value: country || "Canada"}}
                        onChange={(e) => setCountry(e?.value || "Canada")}
                      />
                    </div>
                    <div className="d-flex align-items-start w-100">
                      <RichTextEditor
                        label="Address"
                        content={address}
                        onChange={setAddress}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Date & Symbol Formats Tab */}
              {activeTab === "formats" && (
                <div className={Style.tabPanel}>
                  <div className="d-flex align-items-start w-100 mb-3">
                    <DropDown
                      dropDownwd="w-50 me-3"
                      label="Date Format"
                      options={dateFormats}
                      defaultVal={dateFormat}
                      onChange={setDateformat}
                    />
                    <DropDown
                      dropDownwd="w-50"
                      label="Time Format"
                      options={timeFormats}
                      defaultVal={timeFormat}
                      onChange={setTimeformat}
                    />
                  </div>
                  <div className="d-flex align-items-start w-100 mb-3">
                    <DropDown
                      dropDownwd="w-50 me-3"
                      label="First day of the week"
                      options={days}
                      defaultVal={firstDayWeek}
                      onChange={setFirstDay}
                    />
                    <DropDown
                      dropDownwd="w-50"
                      label="Group Separator"
                      options={groupSeperator}
                      defaultVal={group}
                      onChange={setGroupSeperator}
                    />
                  </div>
                  <div className="d-flex align-items-baseline w-100 mb-3">
                    <DropDown
                      dropDownwd="w-50 me-3"
                      label="Decimal Separator"
                      options={decimalSeperator}
                      defaultVal={decimal}
                      onChange={setDecimalSeperator}
                    />
                    <CustomSelect
                      label="Currency"
                      SelectText="Currency"
                      selectWd="w-50"
                      searchText={()=>{}}
                      options={currencyList}
                      defaultVal={getDefaultValue()}
                      onChange={setCurrency}
                    />
                  </div>
                </div>
              )}

              {/* Security Settings Tab */}
              {activeTab === "security" && (
                <div className={Style.tabPanel}>
                  <div className="d-flex align-items-center justify-content-between mb-2 p-2" style={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                    <span className="text-sm-500">Require Multi-Factor Authentication (MFA)</span>
                    <Switch
                      checked={isMfaRequired}
                      onChange={(ev, data) => setIsMfaRequired(data.checked)}
                      className="ToggleStyle"
                    />
                  </div>
                  <div className="d-flex align-items-center justify-content-between mb-2 p-2" style={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                    <span className="text-sm-500">Require Password Reset on First Login</span>
                    <Switch
                      checked={isFirstLoginResetPassword}
                      onChange={(ev, data) => setIsFirstLoginResetPassword(data.checked)}
                      className="ToggleStyle"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* General Save Button */}
            {permissions && permissions.includes("Account.Edit") && (
              <Button
                btnClass="d_btn d_btn_primarySm mt-4"
                cssClass={Style.btnSave}
                label="Save"
                showLoading={logoLoading}
                clickHandler={handleSave}
                isDisabled={!isAnyInputSet()}
              />
            )}
          </div>
        </div>
      </div>
      {showUploadModal && (
        <UploadImage
          displayImage={settings.logoUrl}
          handleCancel={() => setShowUploadModal(false)}
          uploadImage={updateLogo}
          showLoading={logoLoading}
        />
      )}
    </>
  );
};

export default Settings;
